import React from "react";
import {
  Box,
  Button,
  Dialog,
  Divider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import useAssessmentStore from "components/Assessment/assessmentStore";
import { AssessmentStates } from "components/Assessment/constants";

/**
 * End Session / Submit Assessment (mobile) — mobile equivalent of
 * `Assessment/EndInterview`.
 *
 * Same behaviour as desktop: the button reads "End Session" while anything is
 * unanswered or errored and flips to "Submit Assessment" once everything is in;
 * it's disabled while recording or while an answer is still uploading; and the
 * confirmation copy changes based on what's outstanding.
 */
export default function EndSession({ assessmentState }) {
  const endAssessment = useAssessmentStore((state) => state.endAssessment);
  const questionsError = useAssessmentStore((state) => state.questionsError);
  const uploadStates = useAssessmentStore((state) => state.uploadStates);
  const questionState = useAssessmentStore((state) => state.questionState);
  const questions = useAssessmentStore((state) => state.questions);

  const [openDialog, setOpenDialog] = React.useState(false);

  const show = [
    AssessmentStates.RUNNING,
    AssessmentStates.SHOWING_TUTORIAL,
  ].includes(assessmentState);

  const uploading = uploadStates.find((x) => x === true);
  const disabled = questionState === "RECORDING" || !!uploading;

  const withErrors = questionsError.filter((e) => e).length;
  const unattempted = questions.filter((q) => !q.submitted).length;
  const outstanding = withErrors + unattempted;

  const message = React.useMemo(() => {
    if (!openDialog) return "";

    if (withErrors > 0) {
      return (
        <>
          {withErrors} answer(s) are not recorded properly,
          <br />
          Do you still want to submit?
        </>
      );
    }

    if (unattempted > 0) {
      return (
        <>
          You have not attempted {unattempted} question(s).
          <br />
          {unattempted === questions.length && (
            <>
              No answers recorded, this attempt will not be considered.
              <br />
            </>
          )}
          Do you still want to submit?
        </>
      );
    }

    return (
      <>
        Great! You have answered all the question.
        <br />
        Are you sure to end this interview?
      </>
    );
  }, [openDialog, withErrors, unattempted, questions]);

  if (!show) return null;

  const toggle = () => setOpenDialog((o) => !o);

  return (
    <>
      <Button
        variant={outstanding > 0 ? "outlined" : "contained"}
        size="small"
        disabled={disabled}
        onClick={toggle}
        startIcon={outstanding > 0 ? <CloseIcon sx={{ fontSize: 16 }} /> : null}
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {outstanding > 0 ? "End Session" : "Submit Assessment"}
      </Button>

      <Dialog
        open={openDialog}
        fullWidth
        onClose={toggle}
        PaperProps={{ sx: { m: 3, borderRadius: 2 } }}
      >
        <Box sx={{ px: 4, pt: 4, pb: 3 }}>
          <Typography variant="h6-semiBold" color="neutral.clr-900">
            Finish Interview
          </Typography>
        </Box>

        <Divider sx={{ mx: 4 }} />

        <Box sx={{ p: 4 }}>
          <Typography
            variant="body01-regular"
            color="neutral.clr-900"
            sx={{ display: "block" }}
          >
            {message}
          </Typography>

          <Box sx={{ display: "flex", gap: 3, mt: 5 }}>
            {/* Desktop keeps the safe option visually dominant and the
                destructive one outlined, so mirror that ordering here. */}
            <Button fullWidth variant="contained" onClick={toggle}>
              {outstanding > 0 ? "Go Back" : "No"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => endAssessment(false)}
            >
              {outstanding > 0 ? "Submit anyway" : "Yes"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
